import { useEffect, useState } from 'react'
import { api, ApiError, type CompanyRow } from '../api/client'
import { useAuth } from '../auth/AuthProvider'
import { Button, Field, Input } from '../ui/controls'

/**
 * Кабинет сотрудника платформы: перечень всех компаний.
 *
 * Доступен только роли суперадминистратора. Переход в компанию меняет
 * контекст сессии, после чего сотрудник платформы работает с данными
 * этой компании.
 *
 * Проработаны состояния экрана: загрузка, пустое состояние с подсказкой
 * о следующем действии, ошибка (ДС-10).
 */
export default function PlatformPage() {
  const { switchCompany } = useAuth()
  const [rows, setRows] = useState<CompanyRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [switching, setSwitching] = useState<number | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [form, setForm] = useState<{ name: string; slug: string; brand: string; host: string } | null>(null)
  // Правка вывески: она меняется отдельно от названия компании —
  // одно стоит в документах, другое видит покупатель.
  const [editBrand, setEditBrand] = useState<{ id: number; value: string } | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    let cancelled = false
    api
      .companies()
      .then((list) => {
        if (!cancelled) setRows(list)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setError(e instanceof ApiError ? e.message : 'Не удалось загрузить перечень компаний')
        setRows([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  async function onEnter(id: number) {
    setSwitching(id)
    try {
      await switchCompany(id)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Не удалось перейти в компанию')
    } finally {
      setSwitching(null)
    }
  }

  if (rows === null) {
    return <p className="text-sm text-ink-500">Загрузка перечня компаний…</p>
  }

  return (
    <section>
      <header className="mb-5 flex items-baseline justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-ink-900 dark:text-ink-50">Компании</h2>
          <p className="mt-0.5 text-sm text-ink-500">
            {rows.length === 0
              ? 'Ни одной компании ещё не заведено'
              : `Всего компаний: ${rows.length}`}
          </p>
        </div>
        {form === null && (
          <Button variant="primary" onClick={() => setForm({ name: '', slug: '', brand: '', host: '' })}>
            Новая компания
          </Button>
        )}
      </header>

      {notice && (
        <p className="mb-4 rounded-md border border-success-500/30 bg-success-100 px-3 py-2 text-sm text-success-700 dark:bg-success-700/25 dark:text-success-100">
          {notice}
        </p>
      )}

      {/* Заведение компании.
          Краткое имя предлагается по названию, но остаётся правимым:
          оно попадает в имя поискового индекса и в адреса, и менять его
          потом будет нельзя. */}
      {form !== null && (
        <div className="mb-6 rounded-lg border border-ink-200 bg-white p-4 dark:border-ink-800 dark:bg-ink-950">
          <h3 className="mb-3 text-sm font-semibold text-ink-900 dark:text-ink-50">
            Новая компания
          </h3>
          <div className="grid gap-4 sm:grid-cols-4">
            <Field label="Название">
              <Input
                value={form.name}
                onChange={(e) =>
                  setForm({
                    ...form,
                    name: e.target.value,
                    // Краткое имя подставляется, пока его не правили руками.
                    slug: form.slug === slugify(form.name) ? slugify(e.target.value) : form.slug,
                  })
                }
                placeholder="Мотозапчасти Астана"
                autoFocus
              />
            </Field>
            <Field label="Краткое имя" hint="латиница, цифры и дефис">
              <Input
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })}
                placeholder="motoparts"
                className="font-mono"
              />
            </Field>
            <Field label="Вывеска витрины" hint="Одно слово, видит покупатель">
              <Input
                value={form.brand}
                onChange={(e) => setForm({ ...form, brand: e.target.value })}
                placeholder="Мотозапчасти"
              />
            </Field>
            <Field label="Домен витрины" hint="Необязательно, можно привязать позже">
              <Input
                value={form.host}
                onChange={(e) => setForm({ ...form, host: e.target.value })}
                placeholder="shop.example.kz"
              />
            </Field>
          </div>
          <p className="mt-3 text-xs text-ink-500">
            Компания получит собственный поисковый индекс{' '}
            <span className="font-mono">products-{form.slug || '…'}</span> и основной склад.
            Каталог, атрибуты и товары у неё свои.
          </p>
          <div className="mt-4 flex items-center gap-3">
            <Button
              variant="primary"
              disabled={saving || form.name.trim() === '' || form.slug.trim() === ''}
              onClick={async () => {
                setSaving(true)
                setError(null)
                setNotice(null)
                try {
                  const res = await api.createCompany({
                    name: form.name.trim(),
                    slug: form.slug.trim(),
                    brand: form.brand.trim() || undefined,
                    host: form.host.trim() || undefined,
                  })
                  setRows(await api.companies())
                  setForm(null)
                  setNotice(
                    res.warning ?? `Компания «${res.company.name}» создана. Перейдите в неё, чтобы наполнить каталог.`,
                  )
                } catch (e) {
                  setError(e instanceof ApiError ? e.message : 'Не удалось создать компанию')
                } finally {
                  setSaving(false)
                }
              }}
            >
              {saving ? 'Создание…' : 'Создать'}
            </Button>
            <button
              type="button"
              onClick={() => setForm(null)}
              className="text-sm text-ink-500 hover:text-ink-900 dark:hover:text-ink-50"
            >
              Отмена
            </button>
          </div>
        </div>
      )}

      {error && (
        <p
          role="alert"
          className="mb-4 rounded-md border border-danger-500/30 bg-danger-100 dark:bg-danger-700/25 px-3 py-2 text-sm text-danger-700 dark:text-danger-100"
        >
          {error}
        </p>
      )}

      {rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-ink-300 px-6 py-10 text-center dark:border-ink-700">
          <p className="text-sm text-ink-700 dark:text-ink-300">Компаний пока нет</p>
          <p className="mx-auto mt-1.5 max-w-md text-sm text-ink-500">
            Заведите первую компанию, чтобы она смогла вести собственный каталог
            и витрину на своём домене.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-ink-200 dark:border-ink-800">
          <table className="w-full min-w-[46rem] text-left text-sm">
            <thead className="border-b border-ink-200 bg-ink-50 text-xs uppercase tracking-wide text-ink-500 dark:border-ink-800 dark:bg-ink-900">
              <tr>
                <th scope="col" className="px-4 py-2.5 font-medium">Название</th>
                <th scope="col" className="px-4 py-2.5 font-medium">Вывеска</th>
                <th scope="col" className="px-4 py-2.5 font-medium">Домен витрины</th>
                <th scope="col" className="px-4 py-2.5 font-medium">Индекс</th>
                <th scope="col" className="px-4 py-2.5 text-right font-medium">Сотрудников</th>
                <th scope="col" className="px-4 py-2.5 font-medium">Состояние</th>
                <th scope="col" className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-200 dark:divide-ink-800">
              {rows.map((c) => (
                <tr key={c.id} className="hover:bg-ink-50 dark:hover:bg-ink-900">
                  <td className="px-4 py-3">
                    <div className="font-medium text-ink-900 dark:text-ink-50">{c.name}</div>
                    <div className="font-mono text-xs text-ink-500">{c.slug}</div>
                  </td>
                  <td className="px-4 py-3">
                    {editBrand?.id === c.id ? (
                      <div className="flex items-center gap-2">
                        <Input
                          value={editBrand.value}
                          onChange={(e) => setEditBrand({ id: c.id, value: e.target.value })}
                          autoFocus
                          className="w-40"
                        />
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              await api.setCompanyBrand(c.id, editBrand.value.trim())
                              setRows(await api.companies())
                              setEditBrand(null)
                              setNotice('Вывеска изменена')
                            } catch (e) {
                              setError(e instanceof ApiError ? e.message : 'Не удалось изменить')
                            }
                          }}
                          className="text-xs text-brand-600 hover:underline dark:text-brand-300"
                        >
                          Сохранить
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditBrand(null)}
                          className="text-xs text-ink-500"
                        >
                          Отмена
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setEditBrand({ id: c.id, value: c.brand })}
                        className="text-left text-ink-700 hover:text-brand-600 dark:text-ink-300"
                        title="Изменить вывеску"
                      >
                        {c.brand || <span className="text-ink-400">{c.name}</span>}
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3 text-ink-700 dark:text-ink-300">
                    {c.primary_host || <span className="text-ink-400">не привязан</span>}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-ink-500">{c.search_alias}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-ink-700 dark:text-ink-300">
                    {c.users}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={c.status} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => onEnter(c.id)}
                      disabled={switching !== null}
                      className="rounded-md border border-ink-300 px-3 py-1.5 text-xs font-medium text-ink-700 transition-colors hover:border-brand-500 hover:text-brand-600 disabled:opacity-50 dark:border-ink-700 dark:text-ink-300"
                    >
                      {switching === c.id ? 'Переход…' : 'Перейти'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { text: string; cls: string }> = {
    active: { text: 'Работает', cls: 'bg-success-100 dark:bg-success-700/25 text-success-700 dark:text-success-100' },
    suspended: { text: 'Приостановлена', cls: 'bg-warning-100 dark:bg-warning-700/25 text-warning-700 dark:text-warning-100' },
    archived: { text: 'Закрыта', cls: 'bg-ink-100 text-ink-600' },
  }
  const v = map[status] ?? { text: status, cls: 'bg-ink-100 text-ink-600' }
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${v.cls}`}>
      {v.text}
    </span>
  )
}

/**
 * Краткое имя из названия: латиница, цифры и дефис.
 *
 * Кириллица переводится в латиницу, потому что краткое имя попадает
 * в имя поискового индекса и в адреса, где кириллице не место.
 */
const TRANSLIT: Record<string, string> = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z',
  и: 'i', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r',
  с: 's', т: 't', у: 'u', ф: 'f', х: 'h', ц: 'c', ч: 'ch', ш: 'sh', щ: 'sch',
  ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .split('')
    .map((ch) => (ch in TRANSLIT ? TRANSLIT[ch] : ch))
    .join('')
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
}
